import { Container, Graphics } from 'pixi.js';
import { OFFICE_DOORS } from '@duck/contracts';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { registerScenery, synthCtx } from '../scenery';

// The HERO WELCOME DESK is the room's one bespoke statement furniture piece — register it as an editable
// scenery kind so the level editor can move/delete/re-add it. Its width is baked to the SAME literal the
// decorator places (deskW = 280). signage/floorMedallion stay painted backdrop and are NOT registered.
registerScenery('welcome-desk', (art) => welcomeDesk(synthCtx(art), 280), 'Welcome desk', '🛋️');

/**
 * RECEPTION — the office's front-of-house lobby; the first room a guest meets coming up from the front
 * door. Creative-Director brief (RATIFIED 2026-06-19, ITERATION 2): MINIMALIST but COOL and tastefully
 * STYLIZED. The first pass read close but not minimal enough — STRAY GOLD/YELLOW RUNNER STRIPS (a bold
 * gold kick-bar across the welcome-desk front + gold-heavy floor accents) read as loud floor clutter.
 * This pass KILLS every gold floor runner/strip: gold (art.accent) now appears ONLY as thin tasteful
 * detail lines, never as a big bar. The result is lots of clean negative space, a few perfect statement
 * pieces, perfect alignment, and a clear walking path.
 *
 * The floorplan makes this a LONG, LOW corridor — VERY wide, SHORT (only 160 tall) — so the whole
 * design hugs the walls with LOW furniture and reads as a calm boutique-hotel lobby you pass through:
 *   rect  x[440..1560] y[840..1000], centre (1000,920); interior ~160 tall.
 *   doors N  pos~780  w200 (x680..880, up to the open-space hub)
 *         S  pos~800  w120 (x740..860, DOWN to the entryway / front door — guests arrive here)
 *         S2 pos~1300 w120 (x1240..1360, DOWN to the TOILET — a SECOND south opening on the right)
 *         W  pos~920  w120 (y860..980, across to the game room).
 *
 * COMPOSITION (three statement zones over open floor — nothing crowds the four door lanes):
 *   1. HERO WELCOME DESK — one long, low counter, right-of-centre, hugging the back (N) wall; wood, with
 *      gold only as a hairline ledge pinstripe + a subtle duck-logo floor medallion just in front of it
 *      (NO gold floor runner). Clear of the toilet (S2) door span.
 *   2. WAITING LOUNGE — a low couch + one armchair on ONE small neat runner rug at the sofa anchor
 *      (~620–720,920), on the LEFT between the W door and the entry spine. Both seats are SITTABLE
 *      (see the SEAT ANCHOR LIST in the decorator) and face up toward the desk.
 *   3. SIGNAGE STATEMENT — one clean NEUTRAL "WELCOME" wall plate (gold only as a tiny duck crest + a
 *      hairline underline), centred high on the back wall above the entry spine. The single tasteful
 *      first-impression piece.
 *   …and otherwise CLEAN OPEN FLOOR — a clear walking path from the S entry (x~800) straight up to the
 *   N hub door (x~780). Exactly TWO plants as a SYMMETRIC PAIR flanking the front-entry door.
 *
 * Built ONCE on map build/re-skin (RoomDecorator contract) — static scene, no per-frame work.
 * Everything themes off `art` + the colour helpers so it re-skins per world (café wood → station
 * metal → academy brass).
 */

/**
 * The HERO WELCOME DESK — one long, low hotel-style reception counter read from above: a clean wood
 * counter slab with a slim raised transaction ledge along its FRONT (room-facing) edge and a soft
 * contact shadow. Deliberately understated — no loud gold kick-bar (the CD's "gold runner" complaint);
 * gold survives ONLY as a hairline pinstripe inset into the ledge, a quiet boutique-hotel detail.
 * Origin at the counter's centre; `w` sets its length. Low profile (body within ~42px) for the short room.
 */
function welcomeDesk(ctx: RoomContext, w: number): Container {
  const { art, mix, lighten } = ctx;
  const c = new Container();
  const wood = mix(0x7a5733, art.wall, 0.25);
  const half = w / 2;
  c.addChild(
    new Graphics()
      // soft contact shadow
      .ellipse(0, 24, half + 6, 12)
      .fill({ color: 0x000000, alpha: 0.18 })
      // counter body
      .roundRect(-half, -6, w, 34, 8)
      .fill(wood)
      // slim raised transaction ledge along the FRONT (room-facing) edge
      .roundRect(-half - 4, 20, w + 8, 10, 6)
      .fill(lighten(wood, 0.12))
      .roundRect(-half - 4, 20, w + 8, 3, 6)
      .fill({ color: 0xffffff, alpha: 0.16 })
      // a single HAIRLINE gold pinstripe inset into the ledge — the only gold on the desk, a quiet
      // boutique detail (NOT a floor-runner bar): thin, short of the ends, low alpha.
      .roundRect(-half + 14, 24, w - 28, 1.5, 1)
      .fill({ color: art.accent, alpha: 0.55 })
      // lit top edge so the slab reads volumetric
      .roundRect(-half, -6, w, 3, 8)
      .fill({ color: 0xffffff, alpha: 0.12 }),
  );
  return c;
}

/**
 * The SIGNAGE STATEMENT — one clean wall-mounted "WELCOME" plate with a small soft duck crest above it,
 * the single polished first-impression piece, mounted flat and high on the back (N) wall. Restrained:
 * the plate is a NEUTRAL wall material (themed wood, not a gold mass) with gold surviving only as the
 * tiny duck crest and a single hairline gold underline. Origin at its centre; tiny footprint, hugs wall.
 */
function signage(ctx: RoomContext): Container {
  const { art, mix, lighten } = ctx;
  const c = new Container();
  const plate = mix(0x7a5733, art.wall, 0.25);
  c.addChild(
    new Graphics()
      // small soft duck crest (body + head + beak + eye) floating above the plate — the gold lives HERE
      .ellipse(-4, -16, 13, 8)
      .fill({ color: art.accent, alpha: 0.9 })
      .circle(9, -23, 6)
      .fill({ color: art.accent, alpha: 0.9 })
      .poly([14, -25, 24, -21, 14, -18])
      .fill({ color: lighten(0xffb347, 0.05), alpha: 0.95 })
      .circle(11, -24, 1.6)
      .fill({ color: 0x1b1f29, alpha: 0.85 })
      // one slim NEUTRAL wall plate (not gold) with a soft top highlight so it reads volumetric
      .roundRect(-70, -4, 140, 16, 6)
      .fill(plate)
      .roundRect(-70, -4, 140, 5, 6)
      .fill({ color: 0xffffff, alpha: 0.14 })
      // a single HAIRLINE gold underline — the only gold on the plate, a tasteful thin detail
      .roundRect(-58, 9, 116, 1.5, 1)
      .fill({ color: art.accent, alpha: 0.5 }),
  );
  // faux "WELCOME" lettering — short evenly-spaced neutral ticks (no font here; reads as clean signage).
  const letters = new Graphics();
  for (let i = 0; i < 7; i++) {
    letters.roundRect(-46 + i * 14, 1, 7, 5, 2).fill({ color: lighten(plate, 0.3), alpha: 0.9 });
  }
  c.addChild(letters);
  return c;
}

/**
 * The duck-logo FLOOR MEDALLION — a small, FLAT brand inlay set just in front of the welcome desk: a
 * subtle thin accent ring with a faint duck mark, the quiet "you've arrived" touch the CD asked us to
 * keep. Deliberately SUBTLE — a hairline ring + low-alpha mark, NOT a giant gold ring. Purely flat floor
 * inlay (blocks nothing), so it never crowds the open floor or a door lane. Origin at its centre.
 */
function floorMedallion(ctx: RoomContext): Container {
  const { art, lighten } = ctx;
  const c = new Container();
  c.addChild(
    new Graphics()
      // a single hairline inlaid accent ring (faint — reads as an etched brand mark, not loud gold)
      .circle(0, 0, 20)
      .stroke({ color: art.accent, width: 1.5, alpha: 0.3 })
      // a faint duck mark (body + head + beak), low alpha so it whispers rather than shouts
      .ellipse(-3, 3, 10, 6)
      .fill({ color: art.accent, alpha: 0.4 })
      .circle(7, -3, 4.5)
      .fill({ color: art.accent, alpha: 0.4 })
      .poly([11, -4, 18, -2, 11, 1])
      .fill({ color: lighten(0xffb347, 0.05), alpha: 0.5 }),
  );
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f } = ctx;
  const top = zone.y; // 840
  const bottom = zone.y + zone.height; // 1000
  const midY = (top + bottom) / 2; // 920

  // Door mouths (derive from the contract so dressing rides any geometry shift). There are TWO south
  // openings (front-door ENTRY on the left + the TOILET door on the right), so split them by x.
  const doors = OFFICE_DOORS.filter((d) => d.zoneId === zone.id);
  const nDoor = doors.find((d) => d.side === 'N');
  const sDoors = doors.filter((d) => d.side === 'S').sort((a, b) => a.pos - b.pos);
  const nDoorX = nDoor?.pos ?? 780; // centre of the artery up to the open-space hub
  const sDoorX = sDoors[0]?.pos ?? 800; // centre of the front-door entry (left S opening)

  // ── 1. HERO WELCOME DESK — the room's single statement piece ──────────────────────────────────────
  // One long, low counter hugging the back (N) wall, right-of-centre. Its run sits x~940..1224 so it is
  // well clear (≥16px) of the toilet (S2) door mouth on the far right (x1240..1360) and of the entry
  // spine on the left. The receptionist's chair tucks behind it against the wall; a computer dresses it.
  const deskW = 280;
  const deskX = 1080; // centred across the right pocket, clear of both the spine and the toilet door
  const deskY = top + 36; // hugging the back wall, low in the short room
  place(ctx, welcomeDesk(ctx, deskW), deskX, deskY, 'welcome-desk');
  // A single tidy workstation on the counter (a working but uncluttered reception station).
  place(ctx, f.computer(art), deskX, deskY + 2);
  // The receptionist's swivel chair tucked behind the counter, against the wall (low, reads from above).
  place(ctx, f.officeChair(art), deskX - 70, deskY - 8);
  // The subtle duck-logo floor medallion just in front of the desk — the quiet "you've arrived" mark
  // (a faint etched ring, NOT a gold floor runner — the CD's stray-gold complaint is killed here).
  place(ctx, floorMedallion(ctx), deskX, midY + 22);

  // ── 2. SIGNAGE STATEMENT — one clean backlit "WELCOME" strip on the back wall over the entry spine ──
  // Centred high on the N wall above the walking spine, between the N-door gap and the desk — the single
  // polished first-impression piece. Flush to the wall, small footprint (the room is only ~160 tall).
  place(ctx, signage(ctx), nDoorX, top + 26);

  // ── 3. WAITING LOUNGE — a low couch + one armchair on ONE neat runner rug (the sofa anchor) ─────────
  // Sits on the LEFT, between the W-door mouth (x=440 wall, y860..980) and the entry spine (x>740 kept
  // clear). Both seats are SITTABLE and face UP toward the desk (facing = -Math.PI/2). One small rug only.
  //
  //   SEAT ANCHOR LIST (each waiting seat is sittable):
  //     • reception-sofa     sofa    x=690  y=920  facing=-Math.PI/2 (up)   kind='sofa'
  //     • reception-armchair chair   x=590  y=920  facing=-Math.PI/2 (up)   kind='chair'
  const loungeY = midY; // 920
  // ONE small neat runner rug grounding the cluster (clear of the W-door y-band and the entry spine).
  place(ctx, f.rug(art, 230, 96), 640, loungeY);
  // The low waiting couch at the sofa anchor (~690,920), facing up into the room toward the desk; its
  // right arm ends ~x734, kept clear of the front-entry door span (x740..860) so the entry lane is clean.
  place(ctx, f.sofa(art), 690, loungeY + 6);
  // One soft armchair beside it (the second waiting seat), aligned on the same row.
  place(ctx, f.armchair(art), 590, loungeY + 4);
  // A low round side table ahead of the seats — one small functional accent, not clutter.
  place(ctx, f.coffeeTable(art), 640, loungeY + 36);

  // ── PLANTS — EXACTLY TWO, a symmetric pair flanking the front-entry door (the front-of-house greeting) ─
  // Low/leafy only (no 'tall' — the short room). Placed OUTSIDE the entry-door span (x740..860) so the
  // entry lane stays clear, and clear of the toilet (S2) and N door spans. Perfectly mirrored about x800.
  place(ctx, f.plant(art, 'leafy'), sDoorX - 80, bottom - 28); // left of the entry door (~720)
  place(ctx, f.plant(art, 'leafy'), sDoorX + 80, bottom - 28); // right of the entry door (~880)
};

export default decorate;
